import React from 'react';
import {
  Box,
  Button,
  TableCell,
  styled,
  TableRow,
  Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {NavLink} from 'react-router-dom';
import {getTime} from '../../services/dateService';
import {
  getComparisonFromBaseline,
  getEnergyRatio,
  getEnergyRatioFromFloat,
} from '../../services/measurementService';
import {getIntensityText} from '../../services/playerSessionService';
import styles from '../../styles';
import ProfileIdentity from './ProfileIdentity';
import SessionInfo from './SessionInfo';
import SessionTileStat from './SessionTileStat';
import {Baseline, SessionDetail} from '../../interfaces';
import {SessionTypes} from '../../enums';

const TableCellWrapper = styled(TableCell)(({theme}) => ({
  [theme.breakpoints.down('md')]: {
    display: 'block',
    border: 'none',
  },
  [theme.breakpoints.up('md')]: {
    display: 'table-cell',
  },
}));

interface Props {
  navigateUrl: string;
  type: string;
  hasPlayerData: boolean;
  profileInfo?: {
    firstName?: string;
    lastName?: string;
    position?: string;
    profilePictureUrl?: string;
  };
  startTime?: Date;
  endTime?: Date;
  activeTime?: number;
  timeOnIce?: number;
  energyRatio?: number;
  hustleScore?: number;
  hustleScoreTopLevel?: number;
  loadScore?: number;
  baseline?: Baseline;
  // Grouped rows aggregate several sessions, so summed stats are not
  // comparable against a single-session baseline.
  isGroupedSession?: boolean;
  number?: number;
  teamHasNumbers?: boolean;
  localDisplayStartTime?: string;
  hasVideo?: boolean;
  teamName?: string | null;
  sessionDetail?: SessionDetail;
  timezone?: string;
}

const PlayerSessionListItem: React.FC<Props> = ({
  navigateUrl,
  type,
  hasPlayerData,
  hasVideo,
  profileInfo,
  startTime,
  endTime,
  activeTime,
  timeOnIce,
  energyRatio,
  hustleScore,
  hustleScoreTopLevel,
  loadScore,
  baseline,
  isGroupedSession,
  number,
  teamHasNumbers,
  localDisplayStartTime,
  teamName,
  sessionDetail,
  timezone,
}) => {
  const {t} = useTranslation();

  // Games report a cloud-sourced time on ice; other session types show active time.
  const isGame = type === SessionTypes.game;

  return (
    <TableRow
      sx={{borderBottom: {xs: '1px solid rgb(81, 81, 81)', md: 'none'}}}>
      <TableCellWrapper>
        {profileInfo ? (
          <ProfileIdentity
            firstName={profileInfo.firstName}
            lastName={profileInfo.lastName}
            position={profileInfo.position}
            number={number}
            expandNumberIfEmpty={teamHasNumbers}
            profilePictureUrl={profileInfo.profilePictureUrl}
          />
        ) : (
          <SessionInfo
            sxTitleAndLengthBox={{
              display: {xs: 'flex', md: 'block'},
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
            type={type}
            startTime={startTime}
            endTime={endTime}
            localDisplayStartTime={localDisplayStartTime}
            hasVideo={hasVideo}
            hasPendingPlayerSessions={!hasPlayerData}
            teamName={teamName}
            sessionDetail={sessionDetail}
            timezone={timezone}
          />
        )}
      </TableCellWrapper>
      <TableCellWrapper>
        {hasPlayerData ? (
          <Box sx={{...styles.flexDirectionRow}}>
            <SessionTileStat
              label={
                t(
                  isGame ? 'sessionStats.timeOnIce' : 'sessionStats.activeTime',
                ) as string
              }
              value={getTime(isGame ? timeOnIce : activeTime) ?? '--'}
            />
            <SessionTileStat
              label={t('sessionStats.energyRatio') as string}
              value={
                getEnergyRatioFromFloat(getEnergyRatio({ratio: energyRatio})) ??
                '--'
              }
              comparison={getComparisonFromBaseline({
                stat: getEnergyRatio({ratio: energyRatio}),
                baseline: baseline?.energyRatio,
              })}
            />
            <SessionTileStat
              label={t('sessionStats.hustleScore') as string}
              value={
                hustleScoreTopLevel
                  ? Math.round(hustleScoreTopLevel)
                  : hustleScore
                    ? getIntensityText(hustleScore).toUpperCase()
                    : '--'
              }
              comparison={
                hustleScoreTopLevel
                  ? getComparisonFromBaseline({
                      stat: hustleScoreTopLevel,
                      baseline: baseline?.hustleScoreTopLevel,
                    })
                  : undefined
              }
            />
            <SessionTileStat
              label={t('sessionStats.loadScore') as string}
              value={loadScore != null ? Math.round(loadScore / 100) : '--'}
              comparison={
                isGroupedSession
                  ? undefined
                  : getComparisonFromBaseline({
                      stat: loadScore,
                      baseline: baseline?.cumulative3DPlayerLoadV2,
                    })
              }
            />
          </Box>
        ) : (
          <Typography variant="h4" sx={{...styles.textCenter}}>
            {t('general.pendingUpload')}
          </Typography>
        )}
      </TableCellWrapper>
      <TableCellWrapper align="right">
        {hasPlayerData && (
          <Button component={NavLink} to={navigateUrl}>
            {t('general.details')}
          </Button>
        )}
      </TableCellWrapper>
    </TableRow>
  );
};

export default PlayerSessionListItem;
